import type { AuftragMini, OrderDraftIn, OrderPosition } from "@/lib/orders";
import { getUserDisplayName, type CurrentUser } from "@/lib/auth";
import { resolveMaterialUnit } from "@/lib/materialUnits";

export type OrderLieferart = "lieferung" | "abholung";

export const LOCAL_DRAFT_KEY = "eh.bestellung.draft.v1";
export const LOCAL_DRAFT_ID_KEY = "eh.bestellung.draftId";

export interface OrderSupplierState {
  id: string | number | null;
  name: string;
  email?: string;
  /** Mail am Kontakt, unabhängig vom gewählten Empfänger. */
  company_email?: string;
  anrede?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  person?: string;
}

export interface DraftPosition extends OrderPosition {
  localId: string;
}

export interface OrderWizardState {
  supplier: OrderSupplierState | null;
  templateId: number | null;
  editingOrderId: number | null;
  editingBestellnummerByAuftrag: Record<number, string>;
  editingBestellnummerText: string;
  kuerzel: string;
  auftraege: AuftragMini[];
  positionen: DraftPosition[];
  tkPreview: Record<number, string>;
  lieferadresseId: number | null;
  lieferadresseZhd: string;
  lieferadresseFirma: string;
  lieferadresseStrasse: string;
  lieferadresseHausnummer: string;
  lieferadressePostleitzahl: string;
  lieferadresseStadt: string;
  lieferart: OrderLieferart;
  draftId: number | null;
  sourceMaId: number | null;
  sourceAnfrageId: number | null;
  placeholders: number;
}

export function getAuftragsnummerRaw(a: AuftragMini): string {
  return (a as { auftragsnummer_raw?: string }).auftragsnummer_raw || a.auftragsnummer || "";
}

export function formatAuftragNumberWithYear(input: {
  auftragsnummer?: string | null;
  jahr?: number | null;
  id?: number | string | null;
}): string {
  const num = String(input.auftragsnummer || input.id || "").trim();
  if (!num) return "";
  const yearNum = input.jahr != null ? Number(input.jahr) : null;
  if (yearNum != null && !Number.isNaN(yearNum)) {
    return `${num}-${yearNum}`;
  }
  return num;
}

export function formatAuftragsnummerDisplay(a: AuftragMini): string {
  const nr = getAuftragsnummerRaw(a);
  if (!nr) return "";
  return formatAuftragNumberWithYear({ auftragsnummer: nr, jahr: a.jahr }) || nr;
}

export function formatAuftragHeadline(a: AuftragMini): string {
  const display =
    a.auftragsnummer_display || formatAuftragsnummerDisplay(a) || a.auftragsnummer || "";
  return [display, a.ort, a.beschreibung].filter((s) => s != null && String(s).trim() !== "").join(" ");
}

export function composeLieferadresseString(src: {
  lieferadresseFirma?: string;
  lieferadresseZhd?: string;
  lieferadresseStrasse?: string;
  lieferadresseHausnummer?: string;
  lieferadressePostleitzahl?: string;
  lieferadresseStadt?: string;
  lieferadresse?: string;
}): string {
  const firma = (src.lieferadresseFirma || "").trim();
  const zhd = (src.lieferadresseZhd || "").trim();
  const strasse = (src.lieferadresseStrasse || "").trim();
  const hausnr = (src.lieferadresseHausnummer || "").trim();
  const plz = (src.lieferadressePostleitzahl || "").trim();
  const stadt = (src.lieferadresseStadt || "").trim();
  if (!firma && !strasse && !hausnr && !plz && !stadt) {
    return (src.lieferadresse || "").trim();
  }
  const lines: string[] = [];
  if (firma) lines.push(firma);
  if (zhd) lines.push(`z.Hd. ${zhd}`);
  const street = [strasse, hausnr].filter(Boolean).join(" ").trim();
  if (street) lines.push(street);
  const city = [plz, stadt].filter(Boolean).join(" ").trim();
  if (city) lines.push(city);
  return lines.join("\n");
}

export function getDefaultOrderZhd(user: CurrentUser | null, kuerzel = ""): string {
  const display = getUserDisplayName(user);
  if (display.trim()) return display.trim();
  return (kuerzel || "").trim();
}

export function addressFieldsFromDelivery(addr: {
  id: number;
  firma?: string | null;
  strasse?: string | null;
  hausnummer?: string | null;
  postleitzahl?: string | null;
  stadt?: string | null;
}) {
  return {
    lieferadresseId: addr.id,
    lieferadresseFirma: addr.firma || "",
    lieferadresseStrasse: addr.strasse || "",
    lieferadresseHausnummer: addr.hausnummer || "",
    lieferadressePostleitzahl: addr.postleitzahl || "",
    lieferadresseStadt: addr.stadt || "",
  };
}

export function clearAddressFields() {
  return {
    lieferadresseId: null as number | null,
    lieferadresseFirma: "",
    lieferadresseStrasse: "",
    lieferadresseHausnummer: "",
    lieferadressePostleitzahl: "",
    lieferadresseStadt: "",
  };
}

/** Versandfelder aus GET /orders/:id/delivery-state in den Wizard übernehmen. */
export function deliveryFieldsFromOrderState(src: {
  lieferart?: string | null;
  lieferadresse_id?: number | null;
  lieferadresse_zhd?: string | null;
  lieferadresse_firma?: string | null;
  lieferadresse_strasse?: string | null;
  lieferadresse_hausnummer?: string | null;
  lieferadresse_postleitzahl?: string | null;
  lieferadresse_stadt?: string | null;
}): Pick<
  OrderWizardState,
  | "lieferart"
  | "lieferadresseId"
  | "lieferadresseZhd"
  | "lieferadresseFirma"
  | "lieferadresseStrasse"
  | "lieferadresseHausnummer"
  | "lieferadressePostleitzahl"
  | "lieferadresseStadt"
> {
  const art = String(src.lieferart || "lieferung").toLowerCase();
  if (art === "abholung") {
    return {
      lieferart: "abholung",
      lieferadresseZhd: "",
      ...clearAddressFields(),
    };
  }
  const addrId = Number(src.lieferadresse_id);
  return {
    lieferart: "lieferung",
    lieferadresseId: Number.isFinite(addrId) && addrId > 0 ? addrId : null,
    lieferadresseZhd: String(src.lieferadresse_zhd || ""),
    lieferadresseFirma: String(src.lieferadresse_firma || ""),
    lieferadresseStrasse: String(src.lieferadresse_strasse || ""),
    lieferadresseHausnummer: String(src.lieferadresse_hausnummer || ""),
    lieferadressePostleitzahl: String(src.lieferadresse_postleitzahl || ""),
    lieferadresseStadt: String(src.lieferadresse_stadt || ""),
  };
}

export function newPositionId(): string {
  return `pos_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function buildSupplierId(supplier: OrderSupplierState | null): string | null {
  if (!supplier) return null;
  if (supplier.id != null && String(supplier.id).trim() !== "") return String(supplier.id);
  const name = (supplier.name || "").trim();
  return name ? `name:${name.toLowerCase()}` : null;
}

export function stateToDraftIn(state: OrderWizardState): OrderDraftIn {
  const supplierId = buildSupplierId(state.supplier);
  const anrede =
    (state.supplier?.anrede || "").trim() || "Sehr geehrte Damen und Herren";
  const email = (state.supplier?.email || "").trim();
  const isLieferung = state.lieferart !== "abholung";
  const composedAddress = isLieferung
    ? composeLieferadresseString({
        lieferadresseFirma: state.lieferadresseFirma,
        lieferadresseZhd: state.lieferadresseZhd,
        lieferadresseStrasse: state.lieferadresseStrasse,
        lieferadresseHausnummer: state.lieferadresseHausnummer,
        lieferadressePostleitzahl: state.lieferadressePostleitzahl,
        lieferadresseStadt: state.lieferadresseStadt,
      }).trim()
    : "";

  return {
    supplier_id: supplierId,
    supplier_name: state.supplier?.name || "",
    supplier_contact_first_name: state.supplier?.contact_first_name || "",
    supplier_contact_last_name: state.supplier?.contact_last_name || "",
    supplier_contact_anrede: anrede,
    supplier_contact_name:
      state.supplier?.person ||
      [state.supplier?.contact_first_name, state.supplier?.contact_last_name]
        .filter(Boolean)
        .join(" "),
    supplier_email: email === "" ? null : email,
    template_id: state.templateId,
    kuerzel: state.kuerzel || "",
    auftraege: state.auftraege,
    positionen: state.positionen.map((p) => {
      const rawQty = p.menge;
      const parsedQty = Number(
        typeof rawQty === "string" ? String(rawQty).replace(",", ".").trim() : rawQty,
      );
      return { ...p, menge: Number.isFinite(parsedQty) ? parsedQty : 0, einheit: resolveMaterialUnit(p.einheit) };
    }),
    notizen: "",
    lieferart: state.lieferart,
    lieferadresse: composedAddress || null,
    lieferadresse_id: isLieferung ? state.lieferadresseId : null,
    lieferadresse_zhd: isLieferung ? state.lieferadresseZhd.trim() || null : null,
    lieferadresse_firma: isLieferung ? state.lieferadresseFirma.trim() || null : null,
    lieferadresse_strasse: isLieferung ? state.lieferadresseStrasse.trim() || null : null,
    lieferadresse_hausnummer: isLieferung ? state.lieferadresseHausnummer.trim() || null : null,
    lieferadresse_postleitzahl: isLieferung ? state.lieferadressePostleitzahl.trim() || null : null,
    lieferadresse_stadt: isLieferung ? state.lieferadresseStadt.trim() || null : null,
    source_ma_id: state.sourceMaId ?? null,
    source_anfrage_id: state.sourceAnfrageId ?? null,
  };
}

/** Server-Entwurf-ID aus State oder localStorage (wie in der klassischen App). */
export function resolveDraftId(state: OrderWizardState): number | null {
  const fromState = Number(state.draftId);
  if (Number.isFinite(fromState) && fromState > 0) return fromState;
  try {
    const raw = localStorage.getItem(LOCAL_DRAFT_ID_KEY);
    const fromStorage = raw ? Number(raw) : NaN;
    if (Number.isFinite(fromStorage) && fromStorage > 0) return fromStorage;
  } catch {
    /* ignore */
  }
  return null;
}

export function persistDraftId(draftId: number): void {
  try {
    localStorage.setItem(LOCAL_DRAFT_ID_KEY, String(draftId));
  } catch {
    /* ignore */
  }
}

export function persistLocalDraft(state: OrderWizardState): void {
  try {
    localStorage.setItem(LOCAL_DRAFT_KEY, JSON.stringify(stateToDraftIn(state)));
    const id = resolveDraftId(state);
    if (id) persistDraftId(id);
  } catch {
    /* ignore */
  }
}

export function clearLocalDraft(): void {
  localStorage.removeItem(LOCAL_DRAFT_KEY);
  localStorage.removeItem(LOCAL_DRAFT_ID_KEY);
}

export function restoreLocalDraft(): Partial<OrderWizardState> | null {
  try {
    const raw = localStorage.getItem(LOCAL_DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as OrderDraftIn & { draftId?: number };
    const supplier = d.supplier_id
      ? {
          id: d.supplier_id,
          name: d.supplier_name || "",
          contact_first_name: d.supplier_contact_first_name || "",
          contact_last_name: d.supplier_contact_last_name || "",
          anrede: d.supplier_contact_anrede || "",
          email: d.supplier_email || "",
          person:
            d.supplier_contact_name ||
            [d.supplier_contact_first_name, d.supplier_contact_last_name].filter(Boolean).join(" "),
        }
      : null;

    const auftraege = (Array.isArray(d.auftraege) ? d.auftraege : []).map((a) => ({
      ...a,
      ordnernummer:
        a.ordnernummer ||
        (typeof getAuftragsnummerRaw(a) === "string" ? getAuftragsnummerRaw(a).split("#")[0] : a.ordnernummer),
      nummer:
        a.nummer ??
        (typeof getAuftragsnummerRaw(a) === "string" && getAuftragsnummerRaw(a).includes("#")
          ? Number(getAuftragsnummerRaw(a).split("#")[1])
          : a.nummer),
      auftragsnummer_display: a.auftragsnummer_display || formatAuftragsnummerDisplay(a),
    }));

    const positionen = (Array.isArray(d.positionen) ? d.positionen : []).map((p) => ({
      ...p,
      localId: newPositionId(),
    }));

    const draftIdRaw = localStorage.getItem(LOCAL_DRAFT_ID_KEY);
    return {
      supplier,
      templateId: d.template_id || null,
      auftraege,
      positionen,
      kuerzel: d.kuerzel || "",
      lieferadresseId: d.lieferadresse_id ? Number(d.lieferadresse_id) : null,
      lieferadresseZhd: (d.lieferadresse_zhd || "").toString(),
      lieferadresseFirma: (d.lieferadresse_firma || "").toString(),
      lieferadresseStrasse: (d.lieferadresse_strasse || "").toString(),
      lieferadresseHausnummer: (d.lieferadresse_hausnummer || "").toString(),
      lieferadressePostleitzahl: (d.lieferadresse_postleitzahl || "").toString(),
      lieferadresseStadt: (d.lieferadresse_stadt || "").toString(),
      lieferart: d.lieferart === "abholung" ? "abholung" : "lieferung",
      draftId: draftIdRaw ? Number(draftIdRaw) : null,
    };
  } catch {
    return null;
  }
}

export function emptyWizardState(kuerzel = ""): OrderWizardState {
  return {
    supplier: null,
    templateId: null,
    editingOrderId: null,
    editingBestellnummerByAuftrag: {},
    editingBestellnummerText: "",
    kuerzel,
    auftraege: [],
    positionen: [],
    tkPreview: {},
    lieferadresseId: null,
    lieferadresseZhd: "",
    lieferadresseFirma: "",
    lieferadresseStrasse: "",
    lieferadresseHausnummer: "",
    lieferadressePostleitzahl: "",
    lieferadresseStadt: "",
    lieferart: "lieferung",
    draftId: null,
    sourceMaId: null,
    sourceAnfrageId: null,
    placeholders: 1,
  };
}

/** Projekt der Übersicht/Seite in die Mini-Auswahl des Bestellungs-Assistenten. */
export function auftragMiniFromProject(project: {
  id: number;
  jahr?: number | null;
  ordnernummer?: string | null;
  auftragsnummer?: string | null;
  ort?: string | null;
  beschreibung?: string | null;
}): AuftragMini {
  const raw = String(project.auftragsnummer || "").trim();
  const parts = raw.split("#");
  const mini: AuftragMini = {
    id: project.id,
    jahr: project.jahr ?? new Date().getFullYear(),
    ordnernummer: project.ordnernummer || parts[0] || "",
    nummer: parts.length > 1 ? Number(parts[1]) : null,
    auftragsnummer: raw,
    ort: project.ort,
    beschreibung: project.beschreibung,
  };
  return {
    ...mini,
    auftragsnummer_display: formatAuftragsnummerDisplay(mini),
  };
}

export function buildMailtoHref(to: string, subject: string, body: string): string {
  const bodyCRLF = String(body || "").replace(/\r?\n/g, "\r\n");
  const qp = [
    ["subject", subject],
    ["body", bodyCRLF],
  ]
    .map(([k, v]) => `${k}=${encodeURIComponent(v ?? "")}`)
    .join("&");
  const addr = String(to ?? "").trim();
  if (!addr) return `mailto:?${qp}`;
  return `mailto:${encodeURIComponent(addr)}?${qp}`;
}

export function downloadEml(to: string, subject: string, body: string): void {
  const headers = [`To: ${to || ""}`, `Subject: ${subject || ""}`, `Content-Type: text/plain; charset=UTF-8`, ``].join(
    "\r\n",
  );
  const content = `${headers}${(body || "").replace(/\n/g, "\r\n")}`;
  const blob = new Blob([content], { type: "message/rfc822;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "bestellung.eml";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Artikelnummer hinter die Bezeichnung: „Name Art.-Nr.: 123“. */
export function orderTextWithArticleNumber(
  text: string | null | undefined,
  heroNr: string | null | undefined,
): string {
  const base = String(text ?? "").trim();
  const nr = String(heroNr ?? "").trim();
  if (!nr) return base;
  const escaped = nr.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (new RegExp(`(?<![A-Za-z0-9])${escaped}(?![A-Za-z0-9])`, "i").test(base)) {
    return base;
  }
  const suffix = `Art.-Nr.: ${nr}`;
  if (!base) return suffix;
  const lines = base.split(/\r?\n/);
  const idx = lines.findIndex((line) => line.trim());
  const first = idx >= 0 ? lines[idx] : undefined;
  if (first == null) return suffix;
  lines[idx] = `${first.replace(/\s+$/, "")} ${suffix}`;
  return lines.join("\n");
}

export function orderLinePlainText(html: string): string {
  if (!html) return "";
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || "").replace(/\s+/g, " ").trim();
}

export function computeTkPreview(
  auftraege: AuftragMini[],
  kuerzel: string,
  lastLauf: number,
): Record<number, string> {
  const map: Record<number, string> = {};
  let lauf = lastLauf + 1;
  auftraege.forEach((a) => {
    const raw = getAuftragsnummerRaw(a);
    const nr =
      typeof a.nummer === "number"
        ? a.nummer
        : String(raw || "").includes("#")
          ? Number(String(raw).split("#")[1])
          : NaN;
    const ordner = a.ordnernummer || String(raw || "").split("#")[0] || "";
    const jahrCode = `TK${String(a.jahr).slice(-2)}`;
    const pad = (n: number, d = 3) => String(n).padStart(d, "0");
    map[a.id] = `${jahrCode}-${ordner}#${pad(nr)}-${pad(lauf)}-${(kuerzel || "").toUpperCase()}`;
    lauf += 1;
  });
  return map;
}
